// AgentEventTrail.tsx — the bounded, collapsible activity trail for one agent
// turn, rendered as a human-readable feed (not raw EventView cards).
//
// In the chat feed, completed trails sit ABOVE their assistant reply; the live
// in-progress trail sits below the message list until its reply lands.
//
// Two bounds, for two different failure modes:
//
//   • COLLAPSE WHEN IDLE. At rest the trail is a single summary line, so the
//     assistant's reply is the bottom-most content in the transcript and the
//     composer is reachable BY CONSTRUCTION.
//
//   • CAP THE ROWS WHEN EXPANDED. Only the most recent `maxRows` top-level feed
//     items render, with a one-shot "Show all". Nested sub-agent interiors do
//     not inflate the cap until the user expands them.
//
// While a turn is live the trail auto-expands. It auto-collapses when the turn
// ends unless the user has explicitly toggled it. Nested step bodies follow the
// live cursor (only the latest step stays open) unless "Show all" is checked.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import type { InternalAgentVisibility, ProgressTree, SessionEvent } from '@localagent/sdk';
import {
  mapProgressToActivityFeed,
  countToolInvocations,
  feedItemEventSeqs,
  type ActivityFeedItem,
} from './activity-feed';
import { ActivityFeedList } from './ActivityFeedList';
import { focusedActivityNodeAtom } from './running-work-focus';
import { DEFAULT_MAX_TRAIL_ROWS } from './agent-trail-constants';
import { InternalAgentIdentity } from './InternalAgentIdentity';

/** Stable "nothing rendered" sentinel — see `renderedSeqs` below. */
const NO_SEQS: number[] = [];

export interface AgentEventTrailProps {
  /** Current (or selected) progress tree — drives feed structure + nesting. */
  progressTree: ProgressTree | null;
  internalAgentVisibility?: InternalAgentVisibility;
  /** Session events for correlating tool steps to hydrate seqs. */
  events: readonly SessionEvent[];
  /** Hydrates one event's full `data` on expand (see `ChatSession.getEventData`). */
  getEventData: (seq: number) => Promise<Record<string, unknown>>;
  /** Whether a turn is currently running — drives the default expanded state. */
  isReasoning: boolean;
  /** The turn's raw reasoning text. Folded in here so a turn has ONE disclosure
   *  instead of this trail plus a separate "View reasoning" inside the bubble. */
  thinkingContent?: string;
  /** Top-level feed items shown before "Show all". */
  maxRows?: number;
  /**
   * Reports the `sequence_num`s actually RENDERED, so the caller can scope its
   * batch pre-hydrate to the visible window instead of the whole session.
   */
  onRenderedWindowChange?: (seqs: number[]) => void;
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function feedHasNode(items: readonly ActivityFeedItem[], nodeId: string): boolean {
  for (const item of items) {
    const feed = item.kind === 'sub_agent' ? item.feed : item.kind === 'tool_invocation' ? item.child : undefined;
    if (!feed) continue;
    if (feed.nodeId === nodeId || feedHasNode(feed.items, nodeId)) return true;
  }
  return false;
}

export function AgentEventTrail({
  progressTree,
  internalAgentVisibility = 'friendly',
  events,
  getEventData,
  isReasoning,
  thinkingContent,
  maxRows = DEFAULT_MAX_TRAIL_ROWS,
  onRenderedWindowChange,
}: AgentEventTrailProps) {
  // `null` = follow `isReasoning`; a boolean = the user has decided.
  const [expandedOverride, setExpandedOverride] = useState<boolean | null>(null);
  const [showAll, setShowAll] = useState(false);
  /** Keep every step's nested body open (overrides live auto-collapse). */
  const [showAllDetails, setShowAllDetails] = useState(false);

  const prevReasoning = useRef(isReasoning);
  useEffect(() => {
    if (isReasoning && !prevReasoning.current) {
      setExpandedOverride(null);
      setShowAll(false);
      setShowAllDetails(false);
    }
    prevReasoning.current = isReasoning;
  }, [isReasoning]);

  const expanded = expandedOverride ?? isReasoning;

  const feedItems = useMemo(() => mapProgressToActivityFeed(progressTree), [progressTree]);

  const focused = useAtomValue(focusedActivityNodeAtom);
  useEffect(() => {
    if (!focused || !feedHasNode(feedItems, focused.nodeId)) return;
    setExpandedOverride(true);
    setShowAll(true);
  }, [focused, feedItems]);

  const toolCallCount = useMemo(() => countToolInvocations(feedItems), [feedItems]);
  const internalAgents = useMemo(
    () => progressTree?.internalAgents ?? progressTree?.nodes.flatMap((node) => node.internalAgent ? [node.internalAgent] : []) ?? [],
    [progressTree],
  );

  const visible = useMemo(
    () =>
      showAll || feedItems.length <= maxRows
        ? feedItems
        : feedItems.slice(feedItems.length - maxRows),
    [feedItems, maxRows, showAll],
  );
  const hidden = feedItems.length - visible.length;

  // Collapsed sub-agents do not force hydrate of nested interiors.
  const renderedSeqs = useMemo(() => {
    if (!expanded) return NO_SEQS;
    const seqs: number[] = [];
    for (const item of visible) {
      seqs.push(...feedItemEventSeqs(item, events, false));
    }
    return seqs;
  }, [expanded, visible, events]);

  // Keep the callback in a ref so an inline parent lambda cannot re-fire this
  // effect every render (that was a max-update-depth loop via ChatView setState).
  const onRenderedWindowChangeRef = useRef(onRenderedWindowChange);
  useEffect(() => {
    onRenderedWindowChangeRef.current = onRenderedWindowChange;
  }, [onRenderedWindowChange]);

  useEffect(() => {
    onRenderedWindowChangeRef.current?.(renderedSeqs);
  }, [renderedSeqs]);

  const toggle = useCallback(
    () => setExpandedOverride((prev) => !(prev ?? isReasoning)),
    [isReasoning],
  );

  const hasThinking = Boolean(thinkingContent && thinkingContent.trim());
  const runError = progressTree?.status === 'failed' ? progressTree.error : undefined;
  if (feedItems.length === 0 && !hasThinking && !runError) return null;

  const summary =
    `${feedItems.length} step${feedItems.length === 1 ? '' : 's'} · ${toolCallCount} tool call${toolCallCount === 1 ? '' : 's'}` +
    (hasThinking ? ' · reasoning' : '') +
    (runError ? ' · failed' : '');

  return (
    <div data-testid="agent-event-trail" data-expanded={expanded} className="w-full pl-8">
      <button
        type="button"
        data-testid="agent-trail-toggle"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls="agent-trail-rows"
        className="flex w-full items-center gap-2 rounded px-1 py-1 text-left text-micro text-muted-foreground hover:bg-accent/40"
      >
        <ChevronIcon open={expanded} />
        <span className="font-medium text-foreground">Agent activity</span>
        <span data-testid="agent-trail-summary" aria-live="polite">
          {summary}
        </span>
      </button>

      {expanded && runError && (
        <div data-testid="run-error" className="mx-1 mb-1 rounded bg-destructive/10 p-2">
          <div className="mb-1 text-micro font-medium uppercase text-destructive">Run failed</div>
          <div className="whitespace-pre-wrap break-words font-mono text-micro text-destructive">{runError}</div>
        </div>
      )}

      {expanded && hasThinking && (
        <div data-testid="agent-trail-reasoning" className="mx-1 mb-1 rounded bg-muted p-2">
          <div className="mb-1 text-micro font-medium uppercase text-muted-foreground">Reasoning</div>
          {/* Plain text, never markdown — reasoning is model output, not authored copy. */}
          <div className="whitespace-pre-wrap text-micro text-muted-foreground">{thinkingContent}</div>
        </div>
      )}

      {expanded && (
        <>
          {internalAgents.length > 0 && (
            <div className="space-y-1 px-1 py-1">
              {internalAgents.map((provenance) => (
                <InternalAgentIdentity
                  key={`${provenance.personaName}:${provenance.processId ?? ''}`}
                  provenance={provenance}
                  visibility={internalAgentVisibility}
                />
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 py-1 text-micro text-muted-foreground">
            <label
              data-testid="agent-trail-show-all-details"
              className="inline-flex cursor-pointer items-center gap-1.5 hover:text-foreground"
            >
              <input
                type="checkbox"
                checked={showAllDetails}
                onChange={(e) => setShowAllDetails(e.target.checked)}
                className="size-3 accent-foreground"
              />
              Expand all
            </label>
            {hidden > 0 && (
              <>
                <span data-testid="agent-trail-window-note">
                  Showing last {visible.length} of {feedItems.length} steps
                </span>
                <button
                  type="button"
                  data-testid="agent-trail-show-all"
                  onClick={() => setShowAll(true)}
                  className="rounded px-1.5 py-0.5 font-medium hover:bg-accent/40"
                >
                  Show all steps
                </button>
              </>
            )}
          </div>
          <div
            id="agent-trail-rows"
            data-testid="parent-agent-events"
            aria-live="off"
            className="flex flex-col gap-[3px] pb-1"
          >
            <ActivityFeedList
              items={visible}
              events={events}
              getEventData={getEventData}
              showAllDetails={showAllDetails}
              focusLast={isReasoning && !showAllDetails}
            />
          </div>
        </>
      )}
    </div>
  );
}
